// Trips Service
// Handles trip-related API operations

import { API_BASE_URL } from '../config/environment';
import { authFetch } from '../utils/authFetch';

/**
 * Delete a trip
 * @param {string} tripId - Trip ID to delete
 * @param {string} userId - User ID who owns the trip
 * @returns {Promise<Object>} Deletion result
 */
export const deleteTrip = async (tripId, userId) => {
  try {

    if (!userId) {
      throw new Error('User ID is required to delete a trip');
    }

    const response = await authFetch(`${API_BASE_URL}/trips/${tripId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;

  } catch (error) {
    console.error('Error deleting trip:', error);
    throw error;
  }
};

/**
 * Get all trips for the current user
 * @param {string} userId - User ID to fetch trips for
 * @returns {Promise<Array>} Array of trips
 */
export const getTrips = async (userId) => {
  try {

    if (!userId) {
      throw new Error('User ID is required to fetch trips');
    }

    const response = await authFetch(`${API_BASE_URL}/trips?userId=${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const trips = data.trips || [];

    return trips;

  } catch (error) {
    console.error('Error fetching trips:', error);
    throw error;
  }
};

/**
 * Save/Update a trip
 * @param {Object} trip - Trip object to save
 * @returns {Promise<Object>} Save result
 */
export const saveTrip = async (trip) => {
  try {

    const updatedTrip = {
      ...trip,
      updatedAt: new Date().toISOString()
    };

    const response = await authFetch(`${API_BASE_URL}/trips/${trip.tripId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedTrip)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Clear any localStorage cache for this trip since we now have it in the API
    const cachedTripsJson = localStorage.getItem('travelflow_trips_cache');
    if (cachedTripsJson) {
      try {
        const cachedTrips = JSON.parse(cachedTripsJson);
        const filteredTrips = cachedTrips.filter(t => t.tripId !== trip.tripId);
        if (filteredTrips.length !== cachedTrips.length) {
          localStorage.setItem('travelflow_trips_cache', JSON.stringify(filteredTrips));
        }
      } catch (e) {
        console.warn('Failed to clear cached trip data:', e);
      }
    }
    
    return result;

  } catch (error) {
    console.error('Error saving trip:', error);
    throw error;
  }
};
